<template>
  <div class="page-admin">
    <div class="page-admin__header">
      <div class="page-admin__head">
        <!-- Phones: one screen at a time, the menu or a page of it, which leads back to the menu (or,
             from a page deeper in one, to that page). -->
        <button
          v-if="showsPage && isPhone"
          type="button"
          class="page-admin__back"
          :aria-label="
            parentPath ? $t('page.admin.back-to-section', { section: title }) : $t('page.admin.back-to-settings')
          "
          @click="backToMenu"
        >
          <BaseIcon name="chevron-left" />
        </button>
        <h1 class="page-admin__title">{{ title }}</h1>
      </div>
      <!-- Phones: the page's main action (PageMainAction), as a "+". -->
      <BaseButton
        v-if="isPhone && showsPage && mainAction"
        class="page-admin__main-action"
        has-icon-left
        @click.native="mainAction.run()"
      >
        <BaseIcon :name="mainAction.icon" />
        <span class="page-admin__main-action-label">{{ mainAction.label }}</span>
      </BaseButton>
    </div>
    <div class="page-admin__content">
      <div v-if="!isPhone || !showsPage" class="page-admin__menu">
        <SubSidebarMenu />
      </div>
      <div v-if="!isPhone || showsPage" class="page-admin__page-content">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import SubSidebarMenu from '@/components/layout/SubSidebarMenu';
import { settingsMenu, settingsParentPath, settingsSectionLabel } from '@/components/layout/settings-menu';
import { paths } from '@/util/paths';
import { getPreviousRoute } from '@/util/router-guard';
import { viewport } from '@/util/viewport';

import { mapGetters } from 'vuex';

export default {
  name: 'admin',
  components: {
    BaseButton,
    BaseIcon,
    SubSidebarMenu,
  },
  // The page shown gives its main action for the header (PageMainAction).
  provide() {
    return {
      pageHeader: {
        setAction: (action) => {
          this.mainAction = action;
        },
        clearAction: (action) => {
          if (this.mainAction === action) this.mainAction = null;
        },
      },
    };
  },
  data() {
    return { mainAction: null };
  },
  computed: {
    ...mapGetters('auth', ['currentUser', 'isAdminTenant', 'isAdmin', 'isManager']),
    ...mapGetters('tenant', ['tenant']),
    isPhone() {
      return viewport.size === 'phone';
    },
    // On /admin itself, phones list the settings; any other route is one of their pages.
    showsPage() {
      return this.$route.name !== 'admin';
    },
    menu() {
      return settingsMenu({ isManager: this.isManager, isAdminTenant: this.isAdminTenant, isAdmin: this.isAdmin });
    },
    title() {
      if (!this.isPhone || !this.showsPage) return this.$t('menu.settings');
      const label = settingsSectionLabel(this.menu, this.$route.path);
      return label ? this.$t(label) : this.$t('menu.settings');
    },
    parentPath() {
      return settingsParentPath(this.menu, this.$route.path);
    },
  },
  watch: {
    // Larger screens show a page beside the menu: the first one, if none is picked (also after
    // a phone, on the menu, turns into a tablet).
    showsPage: {
      immediate: true,
      handler: 'showFirstPage',
    },
    isPhone: 'showFirstPage',
  },
  methods: {
    showFirstPage() {
      if (!this.isPhone && !this.showsPage) this.$router.replace(paths.adminInvites).catch(() => {});
    },
    // Back to the menu the reader came from, or to the menu when they came from elsewhere; from a
    // page deeper in a section, to the section's page, the same way.
    backToMenu() {
      const previous = getPreviousRoute();
      if (this.parentPath) {
        if (previous && previous.path === this.parentPath) this.$router.back();
        else this.$router.push(this.parentPath);
      } else if (previous && previous.name === 'admin') this.$router.back();
      else this.$router.push(paths.admin);
    },
  },
};
</script>

<style lang="scss">
.page-admin {
  &__header {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    justify-content: space-between;
    display: flex;
    align-items: center;
    min-height: $header-height;
    position: sticky;
    top: $menu-top-bar-height;
    z-index: 3;
    padding: 0px 32px;
  }

  &__head {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  &__main-action {
    @include phone-main-action;
  }

  &__main-action-label {
    @include visually-hidden;
  }

  &__content {
    display: flex;
    height: calc(var(--viewport-height) - 140px);
    width: 100%;
  }

  &__page-content {
    width: 100%;
    height: 100%;
    overflow: auto;
  }

  &__title {
    color: $color-normal;
    font-family: $font-family-raleway;
    font-size: $font-size-page-title;
    font-weight: normal;
    text-transform: uppercase;
    margin-right: 16px;
  }

  &__back {
    display: grid;
    flex: none;
    place-items: center;
    width: 44px;
    height: 44px;
    margin-left: -12px;
    color: $color-normal;

    .base-icon {
      width: 24px;
      height: 24px;
    }
  }

  // Phones: a compact header, and the page (or the menu) as long as it needs, in the page's scroll.
  &__header {
    @include phone-page-header {
      padding: 0 $phone-page-gutter;
    }
  }

  &__title {
    @include phone-page-title;
  }

  &__content,
  &__page-content {
    @include screen-below('tablet') {
      display: block;
      height: auto;
      overflow: visible;
    }
  }

  // Phones: every settings page pads its content (and its cards) by 32px, from its own styles;
  // here the screen's 16px gutter replaces that, for all of them at once.
  &__page-content {
    @include screen-below('tablet') {
      .page-admin-billing,
      .page-admin-billing__card,
      .page-admin-evaluation-grid,
      .page-admin-field-mappings__content,
      .page-admin-go-nogo__body,
      .page-admin-opportunities__body,
      .page-admin-opportunity-labels__body,
      .page-admin-opportunity-settings,
      .page-admin-opportunity-settings__card,
      .page-admin-scraper-health__content,
      .page-admin-sync-profiles__content,
      .page-admin-task-templates__body,
      .page-admin-tenant,
      .page-admin-tenant-card,
      .page-admin-terminology,
      .page-admin-terminology__card,
      .page-admin-user-profile__extension-options,
      .page-admin-user-profile__main,
      .page-document-keywords__actions,
      .page-document-keywords__header,
      .page-invites__content,
      .page-keywords__content,
      .page-modules__content,
      .page-platform-catalog__content,
      .page-platforms__content,
      .page-pre-projects-import__content,
      .page-tenant-profile,
      .page-tenants__content,
      .page-user-notifications,
      .page-users__content {
        padding-right: $phone-page-gutter;
        padding-left: $phone-page-gutter;
      }

      // The header names the page (its menu label): the page doesn't name it again.
      .page-admin-billing__card--title,
      .page-admin-opportunity-settings__title,
      .page-admin-opportunity-settings__title + .page-admin-opportunity-settings__separator,
      .page-admin-terminology__title {
        display: none;
      }

      .page-admin-terminology__title + .page-admin-terminology__description {
        margin-top: 0;
      }
    }
  }

  &__count {
    color: $color-neutral;
    font-size: $font-size-small;
    padding: 4px 12px;
    text-transform: uppercase;
    margin-bottom: -16px;
  }

  &__refresh {
    // margin-bottom: -16px;
    margin-left: auto;
    // margin-right: 32px;
    width: 40px;

    button {
      z-index: 2;
    }
  }

  &__table-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;

    // Phones: the actions go under the count when they don't fit beside it.
    @include screen-below('tablet') {
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-direction: row;
    justify-content: flex-end;
    margin-left: auto;
  }
}
</style>
